import {api, apiClient} from '@/shared/services/api-client';
import {
  fetchSchedulesPage,
  type ScheduleListParams,
} from '@/shared/services/scheduleRetrieval';
import {extractRows} from '@/shared/services/paginateAll';

const PAYMENT_EXPORT_PAGE_SIZE = 500;
export const PAYMENT_EXPORT_MAX_ROWS = 5000;

/** @deprecated Use `ScheduleListParams` from shared/services/scheduleRetrieval. */
export type PaymentsListSchedulesParams = ScheduleListParams;

export const paymentsApi = {
  listSchedules: (params?: ScheduleListParams) =>
    fetchSchedulesPage(params, 'paymentsApi.listSchedules'),
  /**
   * The per-cycle Payment Approvals feed (one row per payment_requests row,
   * not per schedule) — a session with more than one payment cycle (e.g. a
   * Reschedule partial payment and a later Completion payment) now returns as
   * two independent rows instead of one overwritten schedule_participants row.
   */
  listPaymentRequests: (params?: ScheduleListParams) =>
    apiClient.get('/schedules/payment-requests', {params}),
  /**
   * PM-only export feed. Fetches at most 5,000 filtered payment cycles in
   * backend-sized pages so the UI never mistakes its current page for the full
   * export or sends one oversized request.
   */
  listPaymentRequestsForExport: async (params: ScheduleListParams) => {
    const exportParams = {
      ...params,
      export_data: true,
      limit: PAYMENT_EXPORT_PAGE_SIZE,
    };
    const firstResponse = await apiClient.get('/schedules/payment-requests', {
      params: {...exportParams, page: 1},
    });
    const firstData = firstResponse.data ?? {};
    const firstRows = extractRows(firstData, 'schedules');
    const total = Number(firstData.pagination?.total ?? firstRows.length);
    const pages = Math.min(
      Math.ceil(Math.min(total, PAYMENT_EXPORT_MAX_ROWS) / PAYMENT_EXPORT_PAGE_SIZE),
      10,
    );

    const pageResponses =
      pages > 1
        ? await Promise.all(
            Array.from({length: pages - 1}, (_, index) =>
              apiClient.get('/schedules/payment-requests', {
                params: {...exportParams, page: index + 2},
              }),
            ),
          )
        : [];
    const rows = [
      ...firstRows,
      ...pageResponses.flatMap((response) =>
        extractRows(response.data ?? {}, 'schedules'),
      ),
    ].slice(0, PAYMENT_EXPORT_MAX_ROWS);

    return {
      data: {
        schedules: rows,
        pagination: {
          page: 1,
          limit: PAYMENT_EXPORT_PAGE_SIZE,
          total,
          pages,
        },
      },
    };
  },
  /** Aggregate KPI stats for the Approvals cards (server-computed; moderator-scoped).
   * `bonus_flag` optionally scopes the aggregate to flagged (`true`) or
   * unflagged/plain (`false`) requests only — omit for the project total. */
  stats: (params: {
    project_id: string;
    moderator_id?: string | string[];
    bonus_flag?: boolean;
  }) => apiClient.get('/schedules/payment-stats', {params}),
  /** Distinct Payment Status (OneForma) values for the Payment Status filter dropdown (OD-996). */
  paymentStatusOptions: (params: {project_id: string; moderator_id?: string | string[]}) =>
    apiClient.get('/schedules/payment-status-options', {params}),
  /** Fetch a single schedule (carries payment status) — for polling one record. */
  getSchedule: (id: string) => api.schedules.get(id),
  /** @deprecated Use updatePaymentRequest — schedule id alone can't target one payment cycle once a session has more than one. */
  updateSchedulePayment: (scheduleId: string, data: any) =>
    api.schedules.update(scheduleId, data),
  /** Approve/reject/edit one specific payment cycle, identified by its payment_requests id. */
  updatePaymentRequest: (paymentRequestId: string, data: any) =>
    apiClient.patch(`/payment-requests/${paymentRequestId}`, data),
  /** @deprecated Use retryPaymentRequest — schedule id alone can't target one payment cycle once a session has more than one. */
  retryPayment: (data: { sessionId: string }) =>
    apiClient.post('/payments/session-payment-retry', data),
  /** Retry a failed submission for one specific payment cycle. */
  retryPaymentRequest: (paymentRequestId: string) =>
    apiClient.post(`/payment-requests/${paymentRequestId}/retry`),
  /** Reject or cancel one payment cycle and record the reason (OD-1327). */
  decidePaymentRequest: (
    paymentRequestId: string,
    data: {action: 'reject' | 'cancel'; reason: string},
  ) => apiClient.post(`/payment-requests/${paymentRequestId}/decision`, data),

  listDetailedSessionTypesByProject: (projectId: string) =>
    api.sessionTypes.detailedByProject(projectId),
  listModerators: () => api.moderators.list(),
};
